<script setup>
import { ref, onMounted, useTemplateRef } from 'vue'
import { NotificationApi } from '../../api/NotificationApi'
import { ImpersonationApi } from '../../api/ImpersonationApi'
import NotificationItem from './NotificationItem.vue'

const notifications = ref([])
const page = ref(1)
const loading = ref(false)
const lastPage = ref(1)
const total = ref(0)
const listEl = useTemplateRef('listEl')

async function fetchNotifications() {
    if (page.value > lastPage.value) return

    loading.value = true
    try {
        const data = await NotificationApi.list({ page: page.value })
        notifications.value = notifications.value.concat(data.data)
        lastPage.value = data.meta.last_page
        total.value = data.meta.total
    } finally {
        loading.value = false
    }
}

function fetchNextPage() {
    page.value++
    fetchNotifications()
}

async function destroyNotification(notification) {
    const data = await NotificationApi.destroy(notification.id)
    notifications.value = notifications.value.filter(n => n.id !== notification.id)
    total.value = data.total
    lastPage.value = data.last_page
}

async function destroyAllNotifications() {
    const data = await NotificationApi.destroyAll()
    notifications.value = []
    total.value = data.total
    lastPage.value = data.last_page
    page.value = 0
}

async function accept(notification) {
    if (notification.type === 'impersonation-request') {
        await ImpersonationApi.accept(notification.data.impersonation.id)
        await destroyNotification(notification)
    }
}

async function deny(notification) {
    if (notification.type === 'impersonation-request') {
        await ImpersonationApi.deny(notification.data.impersonation.id)
        await destroyNotification(notification)
    }
}

onMounted(() => {
    fetchNotifications()

    listEl.value.addEventListener('scroll', () => {
        if (listEl.value.scrollTop + listEl.value.clientHeight >= listEl.value.scrollHeight) {
            fetchNextPage()
        }
    })
})
</script>

<template>
    <span
        v-show="total > 0"
        class="tw:absolute tw:top-1 tw:right-1 tw:bg-chili-red-500 tw:text-white tw:text-xs tw:font-normal tw:rounded-full tw:flex tw:items-center tw:justify-center tw:text-center tw:h-4 tw:px-1 tw:pointer-events-none"
        >{{ total }}</span
    >
    <div
        class="tw:fixed tw:top-auto tw:inset-x-0 tw:md:left-auto tw:md:absolute tw:mt-2 tw:w-full tw:md:w-96 tw:md:max-w-96 tw:bg-white tw:border tw:border-[#D8D8D8] tw:rounded tw:shadow-md tw:z-50"
        hidden
        data-dropdown-menu
    >
        <div ref="listEl" class="tw:max-h-[38vh] tw:overflow-y-auto tw:overscroll-contain">
            <div v-if="total > 0">
                <div v-if="total > 1" class="tw:flex tw:justify-end">
                    <button
                        class="tw:m-2 tw:px-3 tw:py-1 tw:rounded-full tw:bg-fuel-yellow-500 tw:text-white tw:text-sm tw:border-none tw:cursor-pointer tw:hover:opacity-80"
                        @click="destroyAllNotifications()"
                    >
                        Tout supprimer
                    </button>
                </div>
                <template v-for="(notification, index) in notifications" :key="notification.id">
                    <NotificationItem
                        :notification="notification"
                        :read="!!notification.read_at"
                        @delete="destroyNotification"
                        @accept="accept"
                        @deny="deny"
                    />
                    <div v-if="index < notifications.length - 1" class="tw:py-2">
                        <div class="tw:h-px tw:bg-cloud-cover-200"></div>
                    </div>
                </template>

                <div v-show="page < lastPage" class="tw:px-5 tw:py-3 tw:text-center">
                    <a href="#" class="tw:text-xs" @click.prevent="fetchNextPage()">Charger plus de notifications</a>
                </div>
            </div>
            <div v-else class="tw:px-5 tw:py-3">
                <p class="tw:text-center tw:text-sm tw:m-0 tw:lg:text-lg">Aucune notification</p>
            </div>
        </div>
    </div>
</template>
